<template>
    <div class="settings-roles-page" :class="{ 'settings-roles-page--embedded': embedded }">
        <div v-if="!embedded" class="settings-roles-page__heading">权限配置</div>
        <div v-if="!hideToolbar" class="settings-roles-page__toolbar">
            <el-button v-if="!hideCreateButton" type="primary" size="small" icon="el-icon-plus" @click="openRoleDialog()">新增角色</el-button>
            <el-button size="small" icon="el-icon-refresh" @click="fetchData">刷新</el-button>
        </div>
        <el-card v-loading="loading" shadow="never" class="settings-roles-page__card">
            <el-table :data="roles" border stripe>
                <el-table-column label="角色名称" min-width="140" align="center" header-align="center">
                    <template slot-scope="{ row }">
                        {{ displayRoleName(row) }}
                    </template>
                </el-table-column>
                <el-table-column prop="code" label="角色编码" min-width="120" align="center" header-align="center" />
                <el-table-column label="类型" width="88" align="center" header-align="center">
                    <template slot-scope="{ row }">
                        <el-tag v-if="Number(row.is_system) === 1" type="info" size="mini">内置</el-tag>
                        <span v-else>自定义</span>
                    </template>
                </el-table-column>
                <el-table-column label="权限数" width="100" align="center" header-align="center">
                    <template slot-scope="{ row }">
                        {{ (row.permissions && row.permissions.length) || 0 }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="260" fixed="right" align="center" header-align="center">
                    <template slot-scope="{ row }">
                        <el-button type="text" size="small" @click="openPermissionDialog(row)">配置权限</el-button>
                        <el-button
                            v-if="Number(row.is_system) !== 1"
                            type="text"
                            size="small"
                            @click="openRoleDialog(row)"
                        >
                            编辑
                        </el-button>
                        <el-button
                            v-if="Number(row.is_system) !== 1"
                            type="text"
                            size="small"
                            class="settings-roles-page__danger"
                            @click="onDeleteRoleClick(row)"
                        >
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <el-dialog
            :title="roleDialogTitle"
            :visible.sync="roleDialogVisible"
            width="480px"
            :close-on-click-modal="false"
            @closed="editingRoleForm = null"
        >
            <el-form ref="roleFormRef" :model="roleForm" :rules="roleRules" label-width="96px" size="small">
                <el-form-item label="角色名称" prop="name">
                    <el-input v-model.trim="roleForm.name" maxlength="100" show-word-limit />
                </el-form-item>
                <el-form-item label="角色编码" prop="code">
                    <el-input
                        v-model.trim="roleForm.code"
                        maxlength="50"
                        show-word-limit
                        :disabled="!!(editingRoleForm && Number(editingRoleForm.is_system) === 1)"
                    />
                </el-form-item>
                <el-form-item label="说明" prop="description">
                    <el-input v-model.trim="roleForm.description" type="textarea" :rows="2" maxlength="500" />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="roleDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="roleSaving" @click="submitRoleForm">保存</el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="配置角色权限"
            :visible.sync="dialogVisible"
            width="820px"
            top="6vh"
            :close-on-click-modal="false"
            append-to-body
            @closed="editingRole = null"
        >
            <div v-if="editingRole" class="settings-roles-page__dialog-role">角色：{{ displayRoleName(editingRole) }}</div>
            <el-input
                v-model.trim="permissionFilter"
                size="small"
                clearable
                placeholder="搜索权限名称或编码"
                prefix-icon="el-icon-search"
                class="settings-roles-page__search app-search-input"
            />
            <el-checkbox
                v-model="selectAllFiltered"
                :indeterminate="selectAllIndeterminate"
                class="settings-roles-page__select-all"
                @change="toggleSelectAllFiltered"
            >
                全选当前筛选结果
            </el-checkbox>
            <div class="settings-roles-page__checkboxes">
                <el-collapse v-model="activeGroupNames">
                    <el-collapse-item
                        v-for="group in filteredPermissionGroups"
                        :key="group.key"
                        :title="groupTitleWithCount(group)"
                        :name="group.key"
                    >
                        <div class="settings-roles-page__group-actions">
                            <el-button type="text" size="mini" @click="selectGroup(group, true)">全选本组</el-button>
                            <el-button type="text" size="mini" @click="selectGroup(group, false)">全不选</el-button>
                        </div>
                        <el-checkbox-group v-model="selectedPermissionIds">
                            <el-checkbox
                                v-for="p in group.items"
                                :key="p.id"
                                :label="p.id"
                                class="settings-roles-page__checkbox"
                            >
                                <span class="settings-roles-page__perm-name">{{ p.name }}</span>
                                <span class="settings-roles-page__perm-code">{{ p.code }}</span>
                                <span v-if="p.description" class="settings-roles-page__perm-desc">{{ p.description }}</span>
                            </el-checkbox>
                        </el-checkbox-group>
                    </el-collapse-item>
                </el-collapse>
            </div>
            <span slot="footer">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="savePermissions">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'SettingsRoles',
    props: {
        embedded: {
            type: Boolean,
            default: false
        },
        hideCreateButton: {
            type: Boolean,
            default: false
        },
        hideToolbar: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            roles: [],
            allPermissions: [],
            dialogVisible: false,
            editingRole: null,
            selectedPermissionIds: [],
            saving: false,
            permissionFilter: '',
            activeGroupNames: [],
            selectAllFiltered: false,
            roleDialogVisible: false,
            editingRoleForm: null,
            roleSaving: false,
            roleForm: {
                name: '',
                code: '',
                description: ''
            },
            roleRules: {
                name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
                code: [{ required: true, message: '请输入角色编码', trigger: 'blur' }]
            }
        };
    },
    computed: {
        roleDialogTitle() {
            return this.editingRoleForm ? '编辑角色' : '新增角色';
        },
        permissionGroups() {
            var map = {};
            var list = this.allPermissions || [];
            var index;
            var p;
            var key;
            var label;

            for (index = 0; index < list.length; index += 1) {
                p = list[index];
                label = (p.group_label && String(p.group_label).trim()) || (p.module || '其他');
                key = label;
                if (!map[key]) {
                    map[key] = { key: key, label: label, items: [] };
                }
                map[key].items.push(p);
            }

            return Object.keys(map).sort().map(function (k) {
                var g = map[k];
                g.items.sort(function (a, b) {
                    var sa = Number(a.sort_order) || 0;
                    var sb = Number(b.sort_order) || 0;
                    if (sa !== sb) {
                        return sa - sb;
                    }
                    return (a.code || '').localeCompare(b.code || '');
                });
                return g;
            });
        },
        filteredPermissionGroups() {
            var q = (this.permissionFilter || '').trim().toLowerCase();
            var groups = this.permissionGroups || [];
            if (!q) {
                return groups;
            }
            return groups.map(function (g) {
                var items = (g.items || []).filter(function (p) {
                    var name = (p.name || '').toLowerCase();
                    var code = (p.code || '').toLowerCase();
                    var desc = (p.description || '').toLowerCase();
                    return name.indexOf(q) !== -1 || code.indexOf(q) !== -1 || desc.indexOf(q) !== -1;
                });
                return {
                    key: g.key,
                    label: g.label,
                    items: items
                };
            }).filter(function (g) {
                return g.items.length > 0;
            });
        },
        filteredFlatIds() {
            var ids = [];
            var groups = this.filteredPermissionGroups || [];
            var gi;
            var ii;
            var g;
            var p;
            for (gi = 0; gi < groups.length; gi += 1) {
                g = groups[gi];
                for (ii = 0; ii < (g.items || []).length; ii += 1) {
                    p = g.items[ii];
                    if (p && p.id != null) {
                        ids.push(p.id);
                    }
                }
            }
            return ids;
        },
        selectAllIndeterminate() {
            var ids = this.filteredFlatIds;
            if (!ids.length) {
                return false;
            }
            var selected = 0;
            var i;
            var id;
            var set = {};
            for (i = 0; i < this.selectedPermissionIds.length; i += 1) {
                set[this.selectedPermissionIds[i]] = true;
            }
            for (i = 0; i < ids.length; i += 1) {
                id = ids[i];
                if (set[id]) {
                    selected += 1;
                }
            }
            return selected > 0 && selected < ids.length;
        }
    },
    watch: {
        permissionFilter() {
            this.syncSelectAllCheckbox();
        },
        selectedPermissionIds() {
            this.syncSelectAllCheckbox();
        }
    },
    created() {
        this.fetchData();
    },
    methods: {
        displayRoleName(role) {
            if (!role) {
                return '';
            }
            if (String(role.code || '') === 'dispatch-specialist-both') {
                return '外派专员-税工';
            }
            return role.name || '';
        },
        groupTitleWithCount(group) {
            return (group.label || group.key) + '（' + (group.items ? group.items.length : 0) + '）';
        },
        syncSelectAllCheckbox() {
            var ids = this.filteredFlatIds;
            if (!ids.length) {
                this.selectAllFiltered = false;
                return;
            }
            var set = {};
            var i;
            for (i = 0; i < this.selectedPermissionIds.length; i += 1) {
                set[this.selectedPermissionIds[i]] = true;
            }
            var ok = true;
            for (i = 0; i < ids.length; i += 1) {
                if (!set[ids[i]]) {
                    ok = false;
                    break;
                }
            }
            this.selectAllFiltered = ok;
        },
        toggleSelectAllFiltered(val) {
            var ids = this.filteredFlatIds;
            var next = (this.selectedPermissionIds || []).slice();
            var set = {};
            var i;
            for (i = 0; i < next.length; i += 1) {
                set[next[i]] = true;
            }
            if (val) {
                for (i = 0; i < ids.length; i += 1) {
                    if (!set[ids[i]]) {
                        next.push(ids[i]);
                    }
                }
            } else {
                var remove = {};
                for (i = 0; i < ids.length; i += 1) {
                    remove[ids[i]] = true;
                }
                next = next.filter(function (id) {
                    return !remove[id];
                });
            }
            this.selectedPermissionIds = next;
        },
        selectGroup(group, checked) {
            var ids = (group.items || []).map(function (p) {
                return p.id;
            });
            var next = (this.selectedPermissionIds || []).slice();
            var set = {};
            var i;
            for (i = 0; i < next.length; i += 1) {
                set[next[i]] = true;
            }
            if (checked) {
                for (i = 0; i < ids.length; i += 1) {
                    if (!set[ids[i]]) {
                        next.push(ids[i]);
                    }
                }
            } else {
                var rm = {};
                for (i = 0; i < ids.length; i += 1) {
                    rm[ids[i]] = true;
                }
                next = next.filter(function (id) {
                    return !rm[id];
                });
            }
            this.selectedPermissionIds = next;
        },
        fetchData() {
            var _this = this;

            this.loading = true;
            Promise.all([
                window.axios.get('/roles'),
                window.axios.get('/permissions')
            ]).then(function (results) {
                _this.roles = (results[0].data && results[0].data.data) || [];
                _this.allPermissions = (results[1].data && results[1].data.data) || [];
                _this.$nextTick(function () {
                    _this.activeGroupNames = _this.permissionGroups.map(function (g) {
                        return g.key;
                    });
                });
            }).catch(function () {
                _this.$message.error('加载角色或权限失败，请确认账号具备相应权限');
            }).finally(function () {
                _this.loading = false;
            });
        },
        openPermissionDialog(role) {
            this.editingRole = role;
            this.selectedPermissionIds = (role.permissions || []).map(function (p) {
                return p.id;
            });
            this.permissionFilter = '';
            this.dialogVisible = true;
            var _this = this;
            this.$nextTick(function () {
                _this.activeGroupNames = _this.permissionGroups.map(function (g) {
                    return g.key;
                });
                _this.syncSelectAllCheckbox();
            });
        },
        openRoleDialog(row) {
            if (row && row.id) {
                this.editingRoleForm = row;
                this.roleForm = {
                    name: row.name || '',
                    code: row.code || '',
                    description: row.description || ''
                };
            } else {
                this.editingRoleForm = null;
                this.roleForm = { name: '', code: '', description: '' };
            }
            this.roleDialogVisible = true;
        },
        onDeleteRoleClick(row) {
            if (!row || !row.id) {
                return;
            }
            if (row.code === 'super-admin') {
                this.$message.warning('超级管理员角色不可删除。');
                return;
            }
            this.confirmDeleteRole(row);
        },
        submitRoleForm() {
            var _this = this;
            this.$refs.roleFormRef.validate(function (valid) {
                if (!valid) {
                    return;
                }
                _this.roleSaving = true;
                var payload = {
                    name: _this.roleForm.name,
                    code: _this.roleForm.code,
                    description: _this.roleForm.description || ''
                };
                var req;
                if (_this.editingRoleForm && _this.editingRoleForm.id) {
                    req = window.axios.put('/roles/' + _this.editingRoleForm.id, payload);
                } else {
                    req = window.axios.post('/roles', payload);
                }
                req.then(function () {
                    _this.$message.success('角色已保存');
                    _this.roleDialogVisible = false;
                    _this.fetchData();
                }).catch(function (err) {
                    var message = err && err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : '保存失败';
                    _this.$message.error(message);
                }).finally(function () {
                    _this.roleSaving = false;
                });
            });
        },
        confirmDeleteRole(row) {
            var _this = this;
            if (!row || !row.id) {
                return;
            }
            this.$confirm('删除后不可恢复，且需先解除该角色下的子账号。是否继续？', '删除角色', { type: 'warning' }).then(function () {
                return window.axios.delete('/roles/' + row.id);
            }).then(function () {
                _this.$message.success('已删除');
                _this.fetchData();
            }).catch(function (err) {
                if (err === 'cancel') {
                    return;
                }
                var message = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : '删除失败';
                _this.$message.error(message);
            });
        },
        savePermissions() {
            var _this = this;

            if (!this.editingRole) {
                return;
            }

            this.saving = true;
            window.axios.put('/roles/' + this.editingRole.id, {
                permission_ids: this.selectedPermissionIds
            }).then(function () {
                _this.$message.success('权限已保存');
                _this.dialogVisible = false;
                _this.fetchData();
            }).catch(function (error) {
                var message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '保存失败';
                _this.$message.error(message);
            }).finally(function () {
                _this.saving = false;
            });
        }
    }
};
</script>

<style scoped>
.settings-roles-page {
    padding: 0;
}

.settings-roles-page--embedded {
    padding: 0;
}

.settings-roles-page__intro--embedded {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 12px;
    line-height: 1.5;
}

.settings-roles-page__heading {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.settings-roles-page__intro {
    margin: 0 0 12px;
    font-size: 13px;
    color: #606266;
    line-height: 1.55;
}

.settings-roles-page__toolbar {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.settings-roles-page__card {
    border: 0;
}

.settings-roles-page__dialog-role {
    margin-bottom: 12px;
    font-weight: 600;
    color: #303133;
}

.settings-roles-page__search {
    margin-bottom: 8px;
    max-width: 320px;
}

.settings-roles-page__select-all {
    display: block;
    margin-bottom: 10px;
}

.settings-roles-page__checkboxes {
    max-height: 520px;
    overflow-y: auto;
}

.settings-roles-page__group-actions {
    margin-bottom: 8px;
}

.settings-roles-page__checkbox {
    display: flex;
    align-items: flex-start;
    margin-left: 0;
    margin-bottom: 8px;
    white-space: normal;
}

.settings-roles-page__perm-name {
    font-weight: 500;
    color: #303133;
}

.settings-roles-page__perm-code {
    margin-left: 6px;
    font-size: 12px;
    color: #909399;
}

.settings-roles-page__perm-desc {
    display: block;
    margin-left: 24px;
    margin-top: 2px;
    font-size: 12px;
    color: #a0a7b3;
    line-height: 1.4;
}

.settings-roles-page__danger {
    color: #f56c6c;
}

.settings-roles-page__muted {
    color: #c0c4cc;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/views/SettingsRoles.vue